/**
 * Off-screen ARIA live region for SR-only announcements.
 *
 * One shared region is enough for the whole app — screen readers serialize
 * announcements in the order they arrive at a single `aria-live` node. Lazy
 * mount on first `announce` keeps the DOM clean when a11y narration isn't
 * being used.
 *
 * Visual-hiding follows the canonical sr-only pattern (1×1, clipped) instead
 * of `display:none` or `visibility:hidden`, both of which suppress SR output.
 *
 * Polite by default: announcements queue behind whatever the SR is reading,
 * which is the right default for move narration / status updates. Pass
 * `{ assertive: true }` only for time-critical interruptions (errors).
 */
import { el } from "./components/dom";

export interface AnnounceOptions {
  /** When true, route to the assertive live region (interrupts SR speech). */
  readonly assertive?: boolean;
}

let politeNode: HTMLElement | null = null;
let assertiveNode: HTMLElement | null = null;

function ensureNode(assertive: boolean): HTMLElement {
  const existing = assertive ? assertiveNode : politeNode;
  if (existing !== null) return existing;
  // Canonical role/aria-live pairs: alert ↔ assertive, status ↔ polite.
  // Explicit aria-live still wins per ARIA spec, but matching the role's
  // implicit live-region semantics keeps validators + older AT happy.
  const node = el("div", {
    class: "sr-live-region",
    attrs: {
      role: assertive ? "alert" : "status",
      "aria-live": assertive ? "assertive" : "polite",
      "aria-atomic": "true",
    },
  });
  // Inline styles so the helper works in any consumer without a CSS dep.
  node.style.position = "absolute";
  node.style.width = "1px";
  node.style.height = "1px";
  node.style.padding = "0";
  node.style.margin = "-1px";
  node.style.overflow = "hidden";
  node.style.clip = "rect(0, 0, 0, 0)";
  node.style.whiteSpace = "nowrap";
  node.style.border = "0";
  document.body.appendChild(node);
  if (assertive) assertiveNode = node;
  else politeNode = node;
  return node;
}

/**
 * Announce text to assistive tech. Replacing the textContent — even with the
 * same string — re-fires the SR announcement, so callers can re-announce
 * unchanged text by clearing first (`announce("")` then `announce(text)`).
 */
export function announce(text: string, opts: AnnounceOptions = {}): void {
  const node = ensureNode(opts.assertive === true);
  // Setting to the same string is a no-op for SRs; toggle through empty to
  // force a re-announcement.
  if (node.textContent === text) node.textContent = "";
  node.textContent = text;
}

/**
 * Reset module state. Test-only — production code never tears down the region.
 */
export function _resetLiveRegionForTests(): void {
  politeNode?.remove();
  assertiveNode?.remove();
  politeNode = null;
  assertiveNode = null;
}
